// COPY of supabase/functions/setup/lib/rules.ts: change the server file first, then copy it here (setupApi.test.ts
// checks the two give the same answers). Used by demo mode (draftFromText) and the set-up screens (keys, route table).
//
// Guided set-up (proposed steps 0.35 "Read your website" and 0.37 "Choose your route"; docs/specs/GUIDED_SETUP.md):
// the contract keys (modules, parts, business types), the default route table, and the deterministic fallback used
// when Claude is unavailable. Pure: no imports, no network, no database.
//
// The app keeps a copy (apps/crm/src/lib/setupRules.ts) so demo mode drafts without the function; a test there checks
// the two give the same answers. Edge Functions are deployed one folder each, so keep the copies in step by hand.

export const MODULES = ["attract", "find", "network", "connect", "reconnect"] as const;
export type ModuleKey = typeof MODULES[number];

export const PARTS = {
  attract: ["website", "content", "social", "newsletter", "ads", "get_found"],
  find: ["sourcing", "email", "letters", "calls", "linkedin", "replies", "tenders"],
  network: ["referrals", "reviews", "events", "partners"],
  connect: ["booking", "quotes", "offers"],
  reconnect: ["keep_warm", "win_back"],
} as const satisfies Record<ModuleKey, readonly string[]>;

/** "find.sourcing", "attract.website", ... every part in the contract. */
export const PART_KEYS: string[] = MODULES.flatMap((m) => PARTS[m].map((p) => `${m}.${p}`));

export const BUSINESS_TYPES = ["b2b_sectors", "local_trade", "adviser", "tenders", "repeat_customers", "unclear"] as const;
export type BusinessType = typeof BUSINESS_TYPES[number];

export const MODULE_STATES = ["on", "off", "later"] as const;
export type ModuleState = typeof MODULE_STATES[number];

export const CONFIDENCE = ["high", "medium", "low"] as const;
export type Confidence = typeof CONFIDENCE[number];

/** The core fields a reading fills; each gets a confidence and a source. */
export const CORE_FIELDS = ["what_we_sell", "who_we_serve", "proof", "voice", "words_to_avoid"] as const;
export type CoreField = typeof CORE_FIELDS[number];

export interface Core {
  what_we_sell: string;
  who_we_serve: string;
  proof: string[];
  voice: { tone: string; examples: string[] };
  words_to_avoid: string[];
  website: string | null;
  business_type: BusinessType;
  /** Where each field came from: a page address, or "owner" for what the owner typed. */
  sources: Partial<Record<CoreField, string>>;
  confidence: Partial<Record<CoreField, Confidence>>;
}

export interface Route {
  goal: string | null;
  first_module: ModuleKey;
  source: "owner" | "suggested";
  reason: string;
}

export interface SetupDraft {
  core: Core;
  business_type: BusinessType;
  route: Route;
  modules: Record<ModuleKey, { state: ModuleState; reason: string }>;
  /** Every part in the contract: what would be switched on when its module is on. */
  parts: Record<string, boolean>;
  drafted_by: "claude" | "rules";
  /** The pages read, in order (empty when the owner typed a few lines instead). */
  pages: { url: string; title: string | null }[];
}

// ---------------------------------------------------------------------------- the default route table
/** Default routes when the owner doesn't know (agreed 11 Oct 2026). Connect is on in every route. */
export const ROUTES: Record<BusinessType, { first_module: ModuleKey; reason: string }> = {
  b2b_sectors: { first_module: "find", reason: "You sell to other businesses in named sectors, so finding and writing to the right firms is the quickest way to new work." },
  local_trade: { first_module: "attract", reason: "Local customers search for what you do, so being easy to find nearby comes first." },
  adviser: { first_module: "network", reason: "Work like yours mostly comes through people who know and trust you, so your network comes first." },
  tenders: { first_module: "find", reason: "You win work by tender, so Find starts by watching for the right tenders." },
  repeat_customers: { first_module: "reconnect", reason: "You have past customers who could buy again, so getting back in touch is the quickest win." },
  unclear: { first_module: "find", reason: "We couldn't tell for sure from what we read, so we suggest starting by finding new customers. You can change this." },
};

export const MODULE_LABELS: Record<ModuleKey, string> = {
  attract: "Attract", find: "Find", network: "Network", connect: "Connect", reconnect: "ReConnect",
};

const CONNECT_REASON = "Always on: it turns interest into booked calls, quotes and offers.";
const LATER_REASON = "Later: switch it on once your first piece is running.";

/** The route for a business type, as a suggestion. */
export function routeFor(type: BusinessType): Route {
  const r = ROUTES[type] ?? ROUTES.unclear;
  return { goal: null, first_module: r.first_module, source: "suggested", reason: r.reason };
}

/** The first module on, Connect always on, every other module later. */
export function defaultModules(first: ModuleKey, firstReason?: string): Record<ModuleKey, { state: ModuleState; reason: string }> {
  const out = {} as Record<ModuleKey, { state: ModuleState; reason: string }>;
  for (const m of MODULES) {
    if (m === "connect") out[m] = { state: "on", reason: CONNECT_REASON };
    else if (m === first) out[m] = { state: "on", reason: firstReason?.trim() || `Your first piece: ${MODULE_LABELS[m]}.` };
    else out[m] = { state: "later", reason: LATER_REASON };
  }
  return out;
}

/** Which parts start switched on when their module is on. Tender work also switches on find.tenders. */
export function defaultParts(type: BusinessType = "unclear"): Record<string, boolean> {
  const on = new Set([
    "attract.website", "attract.content", "attract.get_found",
    "find.sourcing", "find.email", "find.replies",
    "network.referrals", "network.reviews",
    "connect.booking", "connect.quotes",
    "reconnect.keep_warm", "reconnect.win_back",
  ]);
  if (type === "tenders") on.add("find.tenders");
  return Object.fromEntries(PART_KEYS.map((k) => [k, on.has(k)]));
}

// ---------------------------------------------------------------------------- the fallback: keyword rules
/** Keyword patterns per business type. Each match scores one point; tenders needs two to win. */
const SIGNALS: Record<Exclude<BusinessType, "unclear">, RegExp[]> = {
  tenders: [
    /\btenders?\b/, /\btendering\b/, /\bframeworks?\b/, /\bpublic sector\b/, /\bprocurement\b/, /\bcontracts finder\b/,
    /\bfind a tender\b/, /\blocal authorit/, /\bnhs trusts?\b/, /\bcouncils?\b/, /\bbids?\b/, /\bpqq\b|\bitt\b/,
  ],
  b2b_sectors: [
    /\bsectors?\b/, /\bindustries\b/, /\bclients include\b/, /\bcase stud/, /\bb2b\b/, /\bwe work with\b/,
    /\b(businesses|companies|organisations|organizations|employers|manufacturers)\b/, /\bfacilities\b/, /\bcompliance\b/,
    /\binspection\b/, /\btesting\b/, /\bcertification\b/, /\bcare homes?\b/, /\bhousing associations?\b/, /\bschools\b/,
    /\bconstruction\b/, /\blogistics\b/, /\bcontracts?\b/,
  ],
  adviser: [
    /\baccountan/, /\bsolicitors?\b/, /\blaw firm\b/, /\bfinancial (adviser|advisor|planning|planner)s?\b/, /\bwealth\b/,
    /\bconsultan/, /\badvisory\b/, /\bchartered\b/, /\bifa\b/, /\barchitects?\b/, /\btax (advice|returns?)\b/,
    /\bmortgage (adviser|advisor|broker)s?\b/, /\bcoach(ing)?\b/, /\btrusted advis/,
  ],
  local_trade: [
    /\bplumb/, /\belectricians?\b/, /\broof(ing|er)s?\b/, /\bbuilders?\b/, /\bcleaning\b/, /\bsalon\b/, /\bhairdress/,
    /\bdentist/, /\bgarage\b/, /\blandscap/, /\bdecorat/, /\blocksmith/, /\bnear you\b/, /\bfree quote\b/, /\bcall[- ]out\b/,
    /\bareas we cover\b/, /\blocal\b/, /\brestaurant\b/, /\bcafe\b|\bcafé\b/, /\bgym\b/, /\bphysio/, /\bbeauty\b/,
    /\bhomeowners?\b/, /\bdomestic\b/,
  ],
  repeat_customers: [
    /\bbasket\b/, /\badd to (cart|basket)\b/, /\bsubscri(be|ption)\b/, /\bmembership\b/, /\border online\b/, /\bloyalty\b/,
    /\brefills?\b/, /\bfree delivery\b/, /\bshop now\b/, /\bour (shop|store)\b/, /\bre-?order\b/, /\bcheckout\b/,
  ],
};
/** Tie-break order: the earlier type wins a draw. */
const ORDER: Exclude<BusinessType, "unclear">[] = ["tenders", "b2b_sectors", "adviser", "local_trade", "repeat_customers"];

/** Points per business type for a text (lower-cased). */
export function scoreTypes(text: string): Record<Exclude<BusinessType, "unclear">, number> {
  const t = (text ?? "").toLowerCase();
  const out = {} as Record<Exclude<BusinessType, "unclear">, number>;
  for (const k of ORDER) out[k] = SIGNALS[k].filter((re) => re.test(t)).length;
  return out;
}

/** The business type keyword rules suggest: two points at least (three for tenders, which borrows b2b words), else unclear. */
export function classify(text: string): BusinessType {
  const s = scoreTypes(text);
  let best: BusinessType = "unclear";
  let top = 1;
  for (const k of ORDER) {
    const need = k === "tenders" ? 3 : 2;
    if (s[k] >= need && s[k] > top) {
      best = k;
      top = s[k];
    }
  }
  return best;
}

/** Sentences from a text: split on full stops and line breaks, 20 to 300 characters. */
export function sentences(text: string): string[] {
  return (text ?? "")
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 20 && s.length <= 300);
}

const SELL = /\b(we (offer|provide|deliver|sell|make|build|design|supply|install|specialise|specialize|help)|our services|specialists? in|experts? in|providers? of)\b/i;
const SERVE = /\b(we work with|we help|for (businesses|companies|homeowners|families|clients|organisations|organizations|firms)|our clients|serving|customers include|clients include|sectors|industries)\b/i;
const PROOF = /\b(years?|clients?|customers?|projects?|accredit\w*|certified|iso ?\d+|awards?|reviews?|rated|members?|established|since|trusted by)\b/i;
const INFORMAL = /\b(we're|you'll|don't|let's|we'll|you're|get in touch|drop us)\b/i;

/** A draft business core from plain text by simple rules. Everything is marked for the owner to check. */
export function coreFromText(text: string, url?: string | null, type?: BusinessType): Core {
  const all = sentences(text);
  const sell = all.find((s) => SELL.test(s));
  const serve = all.find((s) => SERVE.test(s) && s !== sell);
  const proof = all.filter((s) => /\d/.test(s) && PROOF.test(s)).slice(0, 4).map((s) => s.slice(0, 200));
  const source = url || "owner";
  return {
    what_we_sell: (sell ?? all[0] ?? "").slice(0, 300),
    who_we_serve: (serve ?? "").slice(0, 300),
    proof,
    voice: { tone: INFORMAL.test(text ?? "") ? "warm and informal" : "plain and professional", examples: [] },
    words_to_avoid: [],
    website: url || null,
    business_type: type ?? classify(text),
    sources: Object.fromEntries(CORE_FIELDS.map((f) => [f, source])) as Partial<Record<CoreField, string>>,
    confidence: {
      what_we_sell: sell ? "medium" : "low",
      who_we_serve: serve ? "medium" : "low",
      proof: proof.length ? "medium" : "low",
      voice: "low",
      words_to_avoid: "low",
    },
  };
}

/** The whole set-up draft by rules: business type, core, default route, modules and parts. */
export function draftFromText(text: string, url?: string | null, pages: { url: string; title: string | null }[] = []): SetupDraft {
  const type = classify(text);
  const route = routeFor(type);
  return {
    core: coreFromText(text, url, type),
    business_type: type,
    route,
    modules: defaultModules(route.first_module, route.reason),
    parts: defaultParts(type),
    drafted_by: "rules",
    pages: pages.length ? pages : url ? [{ url, title: null }] : [],
  };
}

// ---------------------------------------------------------------------------- a goal sentence by rules
const GOALS: { module: ModuleKey; re: RegExp; reason: string; type?: BusinessType }[] = [
  { module: "find", type: "tenders", re: /\b(tenders?|frameworks?|bids?|public sector|procurement)\b/i, reason: "You want to win more tenders, so Find starts by watching for the right ones." },
  { module: "reconnect", re: /\b(past|old|previous|existing|former|lapsed) (customers?|clients?)\b|\brepeat\b|\bcome back\b|\bwin (them )?back\b|\bbuy again\b/i, reason: "You want past customers to come back, so ReConnect comes first." },
  { module: "network", re: /\breferr|\bintroduc|\bnetwork|\bword of mouth\b|\bpartners?(hips?)?\b|\brecommend/i, reason: "You want more referrals and introductions, so Network comes first." },
  { module: "attract", re: /\bwebsite\b|\bfound online\b|\bgoogle\b|\bseo\b|\bsearch\b|\bsocial\b|\binstagram\b|\bcontent\b|\bnewsletter\b|\binbound\b|\benquir|\binquir|\bbrand\b|\bads?\b/i, reason: "You want people to find you and get in touch, so Attract comes first." },
  { module: "find", re: /\bnew (customers?|clients?|business)\b|\bleads?\b|\bprospects?\b|\boutbound\b|\bcold\b|\bmeetings?\b|\bpipeline\b|\bsell (to|more)\b|\bcontracts?\b|\bwin (more )?work\b/i, reason: "You want new customers you go out and find, so Find comes first." },
];

/** First module for a goal sentence by keyword rules; when nothing matches, the business type's default route. */
export function routeFromGoal(goal: string, fallbackType: BusinessType = "unclear"): Route & { business_type?: BusinessType } {
  const g = (goal ?? "").trim();
  const hit = GOALS.find((x) => x.re.test(g));
  if (hit) {
    return { goal: g || null, first_module: hit.module, source: "owner", reason: hit.reason, ...(hit.type ? { business_type: hit.type } : {}) };
  }
  const r = routeFor(fallbackType);
  return { ...r, goal: g || null, source: "owner" };
}
